/**
 * RBAC-4.1 — a person's roles, and changing their role, from their record.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the rows mean is `lib/personRole.ts`; who may read or change
 * them is the database's (`20261101d3`: roles.administer, the person homed in the caller's current
 * account, and the users/user_roles triggers for everything a grant is refused for). Nothing here
 * passes a tenant id.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  ASSIGN_PERSON_ROLE_RPC,
  PERSON_ROLE_ASSIGNMENT_RPC,
  assignPersonRoleArgs,
  parsePersonRoleAssignment,
  type PersonRoleAssignment,
} from '@/lib/personRole';
import type { RpcErrorLike } from '@/lib/roleDuplication';

export class PersonRoleError extends Error {
  constructor(public readonly rpc: RpcErrorLike) {
    super(rpc.message ?? 'Unknown error');
  }
}

const toError = (e: { message?: string; code?: string; hint?: string }) =>
  new PersonRoleError({ message: e.message, code: e.code, hint: e.hint });

export async function fetchPersonRoleAssignment(userId: string): Promise<PersonRoleAssignment> {
  const { data, error } = await supabase.rpc(PERSON_ROLE_ASSIGNMENT_RPC, { p_user_id: userId });
  if (error) throw toError(error);
  const parsed = parsePersonRoleAssignment(data);
  if (!parsed) throw new PersonRoleError({ message: "This person's roles came back in a shape this screen does not read." });
  return parsed;
}

/** RBAC-1.5: the reserved permissions `roleId` carries that the caller does not hold. */
export async function fetchReservedNotHeld(roleId: number): Promise<string[]> {
  const { data, error } = await supabase.rpc('reserved_permissions_not_held', { p_role_id: roleId });
  if (error) throw toError(error);
  return Array.isArray(data) ? data.map(String) : [];
}

export async function assignPersonRole(userId: string, roleId: number, shown: PersonRoleAssignment): Promise<void> {
  const { error } = await supabase.rpc(ASSIGN_PERSON_ROLE_RPC, assignPersonRoleArgs(userId, roleId, shown));
  if (error) throw toError(error);
}
